import { Theme } from '@monorepo/relayably/theme'
import { X } from 'phosphor-react-native'
import { useMemo, useState } from 'react'
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

type PlatformGuide = 'ios' | 'android'

export const PhoneForwardingGuideModal = ({
  onClose,
  phoneNumber,
  theme,
  visible,
}: {
  onClose: () => void
  phoneNumber?: string | null
  theme: Theme
  visible: boolean
}) => {
  const styles = useMemo(() => createStyles(theme), [theme])
  const [platformGuide, setPlatformGuide] = useState<PlatformGuide>('ios')
  const forwardingCode = phoneNumber ? `*21*${phoneNumber.replace(/\s+/g, '')}#` : '*21*[your Relayably number]#'
  const disableCode = '##21#'

  return (
    <Modal visible={visible} transparent animationType='fade' onRequestClose={onClose}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <View style={styles.modalHeader}>
            <View style={styles.headerCopy}>
              <Text style={styles.title}>Redirect your phone to Relayably</Text>
              <Text style={styles.supportingCopy}>
                Forward your current number to {phoneNumber || 'your Relayably number'} so Relayably can start answering on your behalf.
              </Text>
            </View>
            <Pressable onPress={onClose} style={styles.iconButton}>
              <X size={18} color={theme.colors.text} weight='bold' />
            </Pressable>
          </View>

          <View style={styles.toggleRow}>
            {([
              { key: 'ios', label: 'iPhone' },
              { key: 'android', label: 'Android' },
            ] as const).map(option => (
              <Pressable
                key={option.key}
                onPress={() => setPlatformGuide(option.key)}
                style={[styles.toggleChip, platformGuide === option.key ? styles.toggleChipActive : undefined]}
              >
                <Text style={[styles.toggleChipText, platformGuide === option.key ? styles.toggleChipTextActive : undefined]}>{option.label}</Text>
              </Pressable>
            ))}
          </View>

          <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} showsVerticalScrollIndicator={false}>
            {platformGuide === 'ios' ? (
              <>
                <Text style={styles.stepTitle}>iPhone steps</Text>
                <Text style={styles.stepCopy}>1. Open the Phone app and go to the keypad.</Text>
                <Text style={styles.stepCopy}>2. Dial the forwarding code below, then tap Call.</Text>
                <View style={styles.codeCard}>
                  <Text style={styles.codeLabel}>Forward all calls</Text>
                  <Text style={styles.codeValue}>{forwardingCode}</Text>
                </View>
                <Text style={styles.stepCopy}>3. Wait for your carrier confirmation, then make a quick test call.</Text>
                <View style={styles.codeCardSecondary}>
                  <Text style={styles.codeLabel}>Turn forwarding off later</Text>
                  <Text style={styles.codeValue}>{disableCode}</Text>
                </View>
                <Text style={styles.noteText}>Some iPhone carriers also expose call forwarding in Settings {'>'} Apps {'>'} Phone {'>'} Call Forwarding.</Text>
              </>
            ) : (
              <>
                <Text style={styles.stepTitle}>Android steps</Text>
                <Text style={styles.stepCopy}>1. Open the Phone app, tap the menu, then go to Settings.</Text>
                <Text style={styles.stepCopy}>2. Open Calling accounts or Supplementary services, then choose Call forwarding.</Text>
                <Text style={styles.stepCopy}>3. Select Always forward and enter {phoneNumber || 'your Relayably number'}.</Text>
                <View style={styles.codeCard}>
                  <Text style={styles.codeLabel}>Alternative GSM code</Text>
                  <Text style={styles.codeValue}>{forwardingCode}</Text>
                </View>
                <View style={styles.codeCardSecondary}>
                  <Text style={styles.codeLabel}>Turn forwarding off later</Text>
                  <Text style={styles.codeValue}>{disableCode}</Text>
                </View>
                <Text style={styles.noteText}>Android menu names vary a bit by manufacturer and carrier, but the forwarding option is usually under the Phone app settings.</Text>
              </>
            )}
          </ScrollView>

          <View style={styles.actionRow}>
            <Pressable onPress={onClose} style={styles.primaryButton}>
              <Text style={styles.primaryButtonLabel}>Got it</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    modalBackdrop: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    modalCard: {
      width: '100%',
      maxWidth: 620,
      maxHeight: '86%',
      borderRadius: 28,
      padding: 22,
      backgroundColor: hexToRgba(theme.colors.surface, 0.98),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      gap: 16,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
    },
    headerCopy: {
      flex: 1,
      gap: 6,
    },
    title: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 30,
      lineHeight: 34,
    },
    supportingCopy: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    iconButton: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.background, 0.7),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
    },
    toggleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    toggleChip: {
      borderRadius: 999,
      paddingHorizontal: 14,
      paddingVertical: 10,
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
    },
    toggleChipActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
      borderColor: hexToRgba(theme.colors.primary, 0.2),
    },
    toggleChipText: {
      color: theme.colors.inactive,
      fontSize: 13,
      fontWeight: '700',
    },
    toggleChipTextActive: {
      color: theme.colors.primary,
    },
    content: {
      flexGrow: 0,
    },
    contentInner: {
      gap: 12,
    },
    stepTitle: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
    },
    stepCopy: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    codeCard: {
      borderRadius: 18,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.16),
      gap: 6,
    },
    codeCardSecondary: {
      borderRadius: 18,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.background, 0.6),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 6,
    },
    codeLabel: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    codeValue: {
      color: theme.colors.text,
      fontSize: 22,
      fontWeight: '800',
    },
    noteText: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
    },
    actionRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
    primaryButton: {
      borderRadius: 16,
      paddingHorizontal: 18,
      paddingVertical: 12,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    primaryButtonLabel: {
      color: theme.colors.staticWhite,
      fontSize: 14,
      fontWeight: '700',
    },
  })
